class InfiniteScroll extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClickHandler.bind(this));

    if (this.dataset.trigger == 'infinite') {
      this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
        rootMargin: '0px 0px 200px 0px'
      });

      this.observer.observe(this);
    }
  }

  onClickHandler() {
    if (this.classList.contains('loading') || this.classList.contains('disabled')) return;

    this.classList.add('loading');
    this.classList.add('disabled');

    const url = this.dataset.url;

    if (!url) return;

    return InfiniteScroll.renderSectionFromFetch(url);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;

    if (InfiniteScroll.isRestoring) return;

    observer.unobserve(this);
    this.onClickHandler();
  }

  static renderSectionFromFetch(url) {
    return fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        InfiniteScroll.renderPagination(responseText);
        InfiniteScroll.renderProductGridContainer(responseText);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  static renderPagination(html) {
    const currentContainer = document
      .getElementById('ProductGridContainer')
      ?.querySelector('.pagination-wrapper');

    const newContainer = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer')
      ?.querySelector('.pagination-wrapper');

    if (!currentContainer) return;

    if (newContainer) {
      currentContainer.innerHTML = newContainer.innerHTML;
    } else {
      currentContainer.remove();
    }
  }

  static renderProductGridContainer(html) {
    const currentGrid = document.getElementById('product-grid');

    const newGrid = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('product-grid');

    if (!currentGrid || !newGrid) return;

    currentGrid.insertAdjacentHTML('beforeend', newGrid.innerHTML);
  }
}

InfiniteScroll.isRestoring = false;

customElements.define('infinite-scroll', InfiniteScroll);

(function () {
  if (!window.sessionStorage) return;
  if (!window.location.pathname.includes('/collections/')) return;

  const productGrid = document.getElementById('product-grid');
  const productGridContainer = document.getElementById('ProductGridContainer');

  if (!productGrid || !productGridContainer) {
    document.documentElement.classList.remove('collection-scroll-restore-pending');
    return;
  }

  const storagePrefix = 'be-yours-collection-scroll:';
  const restoreClass = 'collection-scroll-restore-pending';
  const maxAge = 30 * 60 * 1000;

  function getStorageKey() {
    const url = new URL(window.location.href);

    // Remove page from the key so page 1 / infinite-scroll state uses the same collection key.
    url.searchParams.delete('page');

    return storagePrefix + url.pathname + '?' + url.searchParams.toString();
  }

  function getProductPathFromLink(link) {
    try {
      return new URL(link.href, window.location.origin).pathname;
    } catch (e) {
      return '';
    }
  }

  function findProductLink(productPath) {
    if (!productPath) return null;

    const links = document.querySelectorAll('#product-grid a[href*="/products/"]');

    return Array.from(links).find((link) => {
      return getProductPathFromLink(link) === productPath;
    });
  }

  function getNextInfiniteScrollButton() {
    return document.querySelector('#ProductGridContainer infinite-scroll[data-url]');
  }

  function getNavigationType() {
    const navigationEntry = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];

    if (navigationEntry && navigationEntry.type) {
      return navigationEntry.type;
    }

    if (performance.navigation && performance.navigation.type === 2) {
      return 'back_forward';
    }

    return '';
  }

  function saveCollectionPosition(link) {
    const productPath = getProductPathFromLink(link);

    if (!productPath) return;

    const productItem = link.closest('li');
    const data = {
      productPath: productPath,
      scrollY: window.scrollY || window.pageYOffset || 0,
      itemCount: productGrid.children.length,
      productIndex: productItem ? Array.from(productGrid.children).indexOf(productItem) : -1,
      timestamp: Date.now()
    };

    sessionStorage.setItem(getStorageKey(), JSON.stringify(data));
  }

  function revealCollection() {
    document.documentElement.classList.remove(restoreClass);

    setTimeout(() => {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
    }, 250);
  }

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function waitFrame() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });
  }

  function getSavedData() {
    const savedData = sessionStorage.getItem(getStorageKey());

    if (!savedData) return null;

    try {
      return JSON.parse(savedData);
    } catch (e) {
      sessionStorage.removeItem(getStorageKey());
      return null;
    }
  }

  async function loadUntilProductExists(data) {
    let targetLink = findProductLink(data.productPath);
    let nextButton = getNextInfiniteScrollButton();
    let loadCount = 0;
    const maxLoads = 20;

    while (!targetLink && nextButton && nextButton.dataset.url && loadCount < maxLoads) {
      const nextUrl = nextButton.dataset.url;

      await InfiniteScroll.renderSectionFromFetch(nextUrl);
      await waitFrame();

      targetLink = findProductLink(data.productPath);
      nextButton = getNextInfiniteScrollButton();
      loadCount += 1;
    }

    return targetLink;
  }

  async function restoreCollectionPosition() {
    const data = getSavedData();

    if (!data) {
      revealCollection();
      return;
    }

    // Only use the custom restore after browser Back/Forward.
    // This prevents the collection from restoring an old position on normal page reloads.
    const isBackForward = getNavigationType() === 'back_forward';

    if (!isBackForward) {
      revealCollection();
      return;
    }

    // Ignore old saved positions.
    if (!data.timestamp || Date.now() - data.timestamp > maxAge) {
      sessionStorage.removeItem(getStorageKey());
      revealCollection();
      return;
    }

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    document.documentElement.classList.add(restoreClass);
    InfiniteScroll.isRestoring = true;

    try {
      const targetLink = await loadUntilProductExists(data);

      await waitFrame();

      if (targetLink) {
        const productItem = targetLink.closest('li');

        if (productItem) {
          const rect = productItem.getBoundingClientRect();
          const top = rect.top + window.pageYOffset - 120;

          window.scrollTo({
            top: Math.max(top, 0),
            left: 0,
            behavior: 'auto'
          });
        } else {
          window.scrollTo({
            top: parseInt(data.scrollY, 10) || 0,
            left: 0,
            behavior: 'auto'
          });
        }
      } else {
        window.scrollTo({
          top: parseInt(data.scrollY, 10) || 0,
          left: 0,
          behavior: 'auto'
        });
      }

      await waitFrame();
      revealCollection();

      // One final adjustment after images/layout settle.
      setTimeout(() => {
        const latestTargetLink = findProductLink(data.productPath);

        if (latestTargetLink) {
          const latestProductItem = latestTargetLink.closest('li');

          if (latestProductItem) {
            const rect = latestProductItem.getBoundingClientRect();
            const top = rect.top + window.pageYOffset - 120;

            window.scrollTo({
              top: Math.max(top, 0),
              left: 0,
              behavior: 'auto'
            });
          }
        }
      }, 150);
    } catch (e) {
      console.error(e);
      revealCollection();
    } finally {
      InfiniteScroll.isRestoring = false;
      sessionStorage.removeItem(getStorageKey());
    }
  }

  document.addEventListener('click', function (event) {
    const link = event.target.closest('a[href*="/products/"]');

    if (!link) return;
    if (!productGridContainer.contains(link)) return;

    saveCollectionPosition(link);
  }, true);

  window.addEventListener('pageshow', function (event) {
    // If the browser restores the page from BFCache, the scroll position is usually preserved already.
    // Just reveal the page in case the restore class was added earlier.
    if (event.persisted) {
      revealCollection();
      return;
    }

    restoreCollectionPosition();
  });

  // Safety fallback, so the collection never stays hidden if something unexpected happens.
  setTimeout(revealCollection, 6000);
})();
